import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import Header from 'components/Header/Header';
import { Loading } from '@jg/components';
import config from 'config/config';
import TextInput from 'components/TextInput/TextInput';
import SelectList from 'components/SelectList/SelectList';
import mainActions from 'actions/mainActions/mainActions';
import httpActions from 'actions/httpActions/httpActions';
import apiActions from 'actions/apiActions/apiActions';
import analytics from 'utils/analytics/analytics';
import {
  getSuggestedCharities,
  getCharitiesSearchResponse,
  getParticipantFirstName,
  charitySearchEnabledSelector,
  getCharityId,
  getParticipantEmail,
  getEventId,
  getCharityEntities,
} from 'selectors/apiSelectors/apiSelectors';
import Immutable from 'immutable';
import Tools from 'utils/tools/tools';
import locationUtils from 'utils/locationUtils/locationUtils';

import './CharitySearch.scss';

export class CharitySearch extends React.Component {
  static propTypes = {
    mainActionsBound: PropTypes.object,
    apiActionsBound: PropTypes.object,
    httpActionsBound: PropTypes.object,
    mainStore: PropTypes.object,
    apiStore: PropTypes.object,
    http: PropTypes.object,
    suggestedCharities: PropTypes.object,
    charitiesSearchResponse: PropTypes.object,
    charityEntities: PropTypes.object,
    charitySearchEnabled: PropTypes.bool,
    participantFirstName: PropTypes.string,
    location: PropTypes.object,
    isUserLoggedIn: PropTypes.bool,
    selectedCharityId: PropTypes.string,
    minNumberOfCharsBeforeSearch: PropTypes.number,
    timeKeyStrokeApiRequestGap: PropTypes.number,
    maxNumberOfResults: PropTypes.number,
    participantEmail: PropTypes.string,
    eventId: PropTypes.number,
  };

  static defaultProps = {
    mainActionsBound: {},
    apiActionsBound: {},
    httpActionsBound: {},
    suggestedCharities: Immutable.List(),
    charitiesSearchResponse: Immutable.List(),
    charityEntities: Immutable.Map(),
    charitySearchEnabled: false,
    mainStore: Immutable.fromJS({
      participantPromise: Promise.resolve(),
    }),
    apiStore: Immutable.Map(),
    http: Immutable.Map(),
    participantFirstName: '',
    location: locationUtils.locationStub,
    minNumberOfCharsBeforeSearch: 2,
    timeKeyStrokeApiRequestGap: 800, // milliseconds
    maxNumberOfResults: 5,
  };

  componentDidMount() {
    const { mainStore } = this.props;
    mainStore.get('participantPromise').then(this.firePageView);
  }

  onItemClick(text, extras = {}) {
    const { participantEmail, eventId, location } = this.props;
    analytics.track({
      page_type: 'custom project page',
      event: 'click',
      subtype: 'button',
      event_value: text,
      page_section: 'match my miles > choose your charity',
      activity_id: eventId,
      activity_type: 'event_id',
      email: participantEmail,
      actor_id: location.query.guid,
      actor_sources: 'participant guid',
      ...extras,
    });
  }

  onContinueBtnClick = () => {
    const { participantEmail, eventId, location } = this.props;
    analytics.track({
      page_type: 'custom project page',
      event: 'link click',
      subtype: 'button',
      event_value: 'continue',
      page_section: 'match my miles > choose your charity',
      activity_id: eventId,
      activity_type: 'event_id',
      email: participantEmail,
      actor_id: location.query.guid,
      actor_sources: 'participant guid',
    });

    Tools.scrollToTop();
  }

  onSearchInputClick = () => {
    this.onItemClick('search for a charity');
  }

  getNextRoute() {
    const { isUserLoggedIn, location, selectedCharityId } = this.props;
    const query = `?guid=${location.query.guid}&charityId=${selectedCharityId}`;
    const nextRoute = isUserLoggedIn ? '/invite-friends' : '/login';
    return `${config.ROOT_PATH}${nextRoute}${query}`;
  }

  debouncedDoCharitySearch = Tools.debounce(this.props.apiActionsBound.getCharitySuggestions, 500);

  updateTextQuery = (event) => {
    const {
      mainActionsBound,
      httpActionsBound,
      timeKeyStrokeApiRequestGap,
      minNumberOfCharsBeforeSearch,
    } = this.props;
    const queryText = event.currentTarget.value;
    mainActionsBound.updateQueryText(queryText);
    if (queryText.length < minNumberOfCharsBeforeSearch) {
      return;
    }
    clearTimeout(this.charitySearchTimer);
    this.charitySearchTimer = setTimeout(() => {
      httpActionsBound.requestMade('charitySearch');
      this.debouncedDoCharitySearch();
    }, timeKeyStrokeApiRequestGap);
  }

  selectCharity = (event) => {
    const { mainActionsBound, charityEntities } = this.props;
    event.preventDefault();
    const charityId = event.currentTarget.getAttribute('data-id');
    mainActionsBound.clearQueryText();
    mainActionsBound.setCharity(charityId);

    if (Tools.localStorageAvailable()) {
      const entity = JSON.stringify(charityEntities.get(charityId).toJS());
      window.localStorage.setItem('selectedCharityEntity', entity);
    }

    this.onItemClick('charity', {
      content_id: charityId,
      content_type: 'charity_id',
      charity_id: charityId,
    });
  }

  changeCharity = (event) => {
    const { mainActionsBound } = this.props;
    event.preventDefault();
    mainActionsBound.resetCharitySelection();
    this.onItemClick('choose a different charity');
  }

  clearInput = (event) => {
    const { mainActionsBound } = this.props;
    event.preventDefault();
    mainActionsBound.clearQueryText();
  }

  firePageView = () => {
    const { participantEmail, eventId, location } = this.props;
    analytics.track({
      page_type: 'custom project page',
      event: 'page view',
      subtype: 'match my miles',
      event_value: 'choose your charity',
      activity_id: eventId,
      activity_type: 'event_id',
      email: participantEmail,
      actor_id: location.query.guid,
      actor_sources: 'participant guid',
    });
  }

  render() {
    const {
      mainStore,
      apiStore,
      http,
      suggestedCharities,
      charitiesSearchResponse,
      participantFirstName,
      charitySearchEnabled,
      selectedCharityId,
      maxNumberOfResults,
    } = this.props;

    const charityEntities = apiStore.getIn(['normalized', 'entities', 'charities'], Immutable.Map());
    const charitiesSearchResponseJS = charitiesSearchResponse.toJS().slice(0, maxNumberOfResults);

    const selectedCharity = [selectedCharityId].map(() => charityEntities.get(`${selectedCharityId}`, Immutable.Map()).toJS());
    const queryText = mainStore.get('queryText');

    return (
      <div className="charity-search">
        <Header step="1" />
        <div className="charity-search__body container">
          <p className="charity-search__hey">Hey {participantFirstName}!</p>

          {suggestedCharities.size === 1 && !charitySearchEnabled ? (
            <p className="charity-search__which-charity">You are raising funds for</p>
          ) : (
            <p className="charity-search__which-charity">Which charity are you supporting?</p>
          )}

          {charitySearchEnabled && (
            <div className="charity-search__input">
              <TextInput
                icon={queryText ? '' : 'magnify'}
                placeholder="Search for a charity&hellip;"
                onChange={this.updateTextQuery}
                onClick={this.onSearchInputClick}
                value={queryText}
                clearInput={this.clearInput}
              />
              { queryText && (
                <div>
                  { http.getIn(['charitySearch', 'isInFlight']) ? (
                    <div className="charity-search__input__no-results">
                      <Loading size="small" />
                    </div>
                  ) : (
                    <div className="charity-search__input__select-charity-overlay">
                      { charitiesSearchResponseJS.length > 0 && (
                        <SelectList
                          onSelect={this.selectCharity}
                          items={charitiesSearchResponseJS}
                          labelKey="charityName"
                          idKey="charityId"
                        />
                      )}
                      { !charitiesSearchResponseJS.length && (
                        <div className="charity-search__input__no-results">There are no results for your search</div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className={`charity-search__select-charity ${queryText && '--searching'}`}>
            { selectedCharityId ? (
              <SelectList
                showRadioIcon
                selectedId={Number(selectedCharityId)}
                onSelect={this.selectCharity}
                items={selectedCharity}
                labelKey="charityName"
                idKey="charityId"
              />
            ) : (
              <SelectList
                showRadioIcon
                onSelect={this.selectCharity}
                items={suggestedCharities.toJS()}
                labelKey="charityName"
                idKey="charityId"
              />
            )}
          </div>

          { selectedCharityId && suggestedCharities.size > 1 && <a href="" role="button" onClick={this.changeCharity} className="charity-search__change-charity-btn">Change charity</a> }

          <div className="charity-search__continute-btn__wrapper">
            <Link to={this.getNextRoute()} className={`charity-search__continute-btn ${!selectedCharityId && 'is-disabled'}`} onClick={this.onContinueBtnClick}>Continue</Link>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  participantEmail: getParticipantEmail(state),
  eventId: getEventId(state),
  mainStore: state.mainStore,
  charityEntities: getCharityEntities(state),
  http: state.http,
  apiStore: state.api,
  isUserLoggedIn: state.mainStore.get('isUserLoggedIn'),
  suggestedCharities: getSuggestedCharities(state),
  charitiesSearchResponse: getCharitiesSearchResponse(state),
  participantFirstName: getParticipantFirstName(state),
  charitySearchEnabled: charitySearchEnabledSelector(state),
  selectedCharityId: getCharityId(state),
});

const mapDispatchToProps = (dispatch) => ({
  mainActionsBound: bindActionCreators(mainActions, dispatch),
  apiActionsBound: bindActionCreators(apiActions, dispatch),
  httpActionsBound: bindActionCreators(httpActions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(CharitySearch);



// WEBPACK FOOTER //
// ./layouts/CharitySearch/CharitySearch.js